import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Form, json, Link, Navigate, replace, useActionData, useNavigate, useSubmit, useSearchParams } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import './status.css'

// Capacitor
import { OneSignal } from '@capacitor/one-signal'
import { Share } from '@capacitor/share'

// COMPONENTS
import * as Icons from '@icons'
import { Column } from '@components/templates/column'
import { Separator } from '@components/shadcn/separator'
import AnchorLink from '@components/atoms/anchor-link'
import Avatar from '@components/molecules/avatar'
import CollapsibleElement from '@components/atoms/collapsable-element'
import Divider from '@components/atoms/divider'
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from "@/components/shadcn/drawer"
import GroupBanner from '@components/organisims/group-banner'
import GroupCell from '@components/molecules/group-cell'
import GroupCard from '@components/organisims/group-card'
import MerchantLogo from '@components/atoms/merchant-logo'
import PaymentMethodBrand from '@components/atoms/payment-method-brand'
import ProgressBar from '@components/atoms/progressbar'
import PledgeList from '@components/organisims/pledge-list'
import Button from '@components/atoms/button'
import AddToWalletButton from '@components/atoms/add-to-wallet-button'
import Card from '@components/atoms/card'
import Switch from '@components/atoms/switch'
import GroupPromoChecklist from '@components/molecules/group-promo-checklist'
import ServiceOutageModal from '@components/organisims/service-outage-modal'
import Spinner from '@components/atoms/spinner'
import TransactionLedgerEditContribution from '@components/molecules/transaction-ledger/edit-contribution'
import TransactionLedgerEditMerchant from '@components/molecules/transaction-ledger/edit-merchant'
import TransactionLedgerCloseGroup from '@components/molecules/transaction-ledger/close-group'
import ErrorModal from '@components/molecules/error-modal'
import InactivityNudge from '@/components/molecules/nudges/inactivity-nudge'
import AdjustLedgerNudge from '@/components/molecules/nudges/adjust-ledger-nudge'
import LiveActivityFeed from '../../../../components/organisims/live-activity-feed'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { parseTeraUserData, parseUserLimits } from '@schemas'
import { createResponseObj, isFailedResponse, VirtualCardAPI, FailedResponse } from '@apis'
import { createAuthenticatedLoader } from '@utils/loaders'
import { isNonEmptyString } from '@utils/type-checkers'
import { getGroupStatus, toggleBodyScroll } from '@utils/temp-helpers'
import { priceFormatter } from '@utils/number-helper'
import { createAction } from '@utils/actions'
import { useShareSheet } from '@components/organisims/share-card'
import { useGroupLoaderData, usePledgeOutletContext } from '../'
import { useGlobalSettings } from '../../../_globalStore/_hooks'
import { useGroupSettings } from '../../_localStore/_hooks'
import { useHasHistoricalGroups } from '@hooks/tanstack/useHasHistoricalGroups'
import { useUserCredits } from '@hooks/tanstack/useUserCredits'
import { format, differenceInDays, max, parseISO } from 'date-fns'
import { platform } from '@utils/capacitor-helper'
import { queryClient } from '@/lib/queryClient'
import { isNudgeDismissed as checkNudgeDismissed, saveNudgeDismissal } from '@utils/nudge-dismissal'
import { isUnclaimedCredit } from '@utils/promo-helper'
import { getTransactionSpend } from '@components/molecules/transaction-ledger/helpers'
import { toast } from 'sonner'
import { isDemoGroup } from '../demo-data'
import DemoWalkthrough, { type DemoWalkthroughStep, type DemoWalkthroughTargets } from './_components/DemoWalkthrough'

const isSplitcardCreditTransactionGroup = (transactionId?: string, relatedTransactions?: Transaction[]) => {
    if (transactionId === 'splitcard-credit') return true

    return !!relatedTransactions?.some(transaction => transaction.merchant?.name === 'Splitcard Credit')
}

const GroupStatusScreen: FC<{}> = () => {
    const { isSignedIn, user } = useUser()
    const { createToast, state } = useGroupSettings()
    const { isEditingParticipant } = state
    const { state: { isIssuingCard, isServicable }, setIsIssuingCard } = useGlobalSettings()
    const [{ lockedSlots }] = usePledgeOutletContext()
    const group = useGroupLoaderData()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const userLimits = useMemo(() => parseUserLimits(user?.publicMetadata?.limits), [user?.publicMetadata?.limits])
    const canCreateGroups = userLimits.createGroups
    const canAddAdditionalFunds = userLimits.addAdditionalFunds
    const canJoinGroups = userLimits.joinGroups
    const isDemoMode = isDemoGroup(group.id)

    const isMerchant = !!user?.publicMetadata.merchant && !!group.merchant && user?.publicMetadata.merchant?.id === group.merchant?.id
    const isOrganizer = isSignedIn ? group.organizer?.id === user.id : false
    const { isLocked, groupStatus: rawGroupStatus } = getGroupStatus(group)
    const groupStatus = (!isOrganizer && rawGroupStatus === 'pending') ? 'completed' : rawGroupStatus
    const organizerName = !!group.organizer ? `${group.organizer.givenName || ''} ${group.organizer.familyName || ''}`.trim() : ''
    const demoGroupOverviewRef = useRef<HTMLDivElement>(null)
    const demoVirtualCardRef = useRef<HTMLDivElement>(null)
    const demoGroupGoalRef = useRef<HTMLDivElement>(null)
    const demoLiveActivityRef = useRef<HTMLDivElement>(null)
    const demoTransactionsRef = useRef<HTMLDivElement>(null)
    const demoParticipantsRef = useRef<HTMLDivElement>(null)
    const demoManageGroupRef = useRef<HTMLButtonElement>(null)

    const activeGroupId = useMemo(() => {
        if (group.card?.cardType === 'physical') return user?.publicMetadata.tera?.physicalCard?.activeGroup
        else if (group.card?.id === user?.publicMetadata.tera?.virtualCard?.id) return user?.publicMetadata.tera?.virtualCard?.activeGroup
        else return ''
    }, [group.card?.cardType, user?.publicMetadata.tera])
    const [activeGroup, setActiveGroup] = useState<Group>()
    const [gettingActiveGroup, setGettingActiveGroup] = useState(false)
    const [closingActiveGroup, setClosingActiveGroup] = useState(false)

    const [isManageGroupDrawerOpen, setIsManageGroupDrawerOpen] = useState(false)
    const [ledgerTransactionId, setLedgerTransactionId] = useState<string | undefined>('')
    const [isContributionMode, setIsContributionMode] = useState(false)

    const actionData = useActionData()
    const [showOutage, setServiceOutage] = useState(false)
    const [showError, setError] = useState(false)
    const [error, setErrorData] = useState<FailedResponse>()
    const [addMoreAmount, setAddMoreAmount] = useState(0)
    const [addMoreMerchant, setAddMoreMerchant] = useState('')
    const [isCardReady, setIsCardReady] = useState(false)

    const [isInviteDrawerOpen, setIsInviteDrawerOpen] = useState(false)
    const qRCodeRef = useRef<HTMLDialogElement>(null)
    function openQRCodeConfirm() { if (qRCodeRef.current) qRCodeRef.current.showModal() }
    function closeQRCodeConfirm() { if (qRCodeRef.current) qRCodeRef.current.close() }

    const [isNudgeDismissed, setIsNudgeDismissed] = useState(true)
    const [closeGroupKey, setCloseGroupKey] = useState(0)
    const [currentDemoStepId, setCurrentDemoStepId] = useState('')
    const [isDemoTourDismissed, setIsDemoTourDismissed] = useState(false)
    const [isDemoActivityFeedOpen, setIsDemoActivityFeedOpen] = useState(group.type === 'merchant' && !isMerchant)
    const [isCloseGroupDialogOpen, setIsCloseGroupDialogOpen] = useState(false)

    const demoTargets = useMemo<DemoWalkthroughTargets>(() => ({
        groupOverview: demoGroupOverviewRef,
        virtualCard: demoVirtualCardRef,
        groupGoal: demoGroupGoalRef,
        liveActivity: demoLiveActivityRef,
        transactions: demoTransactionsRef,
        participants: demoParticipantsRef,
        manageGroup: demoManageGroupRef,
    }), [])

    const demoWalkthroughSteps = useMemo<DemoWalkthroughStep[]>(() => ([
        {
            id: 'welcome',
            targetId: 'groupOverview',
            title: 'Welcome to Your Demo Group',
            description: "This is exactly what you'll see when you create your own group. Let's walk through the experience from top to bottom.",
            placement: 'bottom',
            nextLabel: "Let's Go",
        },
        {
            id: 'groupOverview',
            targetId: 'groupOverview',
            title: 'Group Overview',
            description: 'Start here. This section shows the group name, what has been collected, and the overall status at a glance.',
            placement: 'bottom',
        },
        {
            id: 'virtualCard',
            targetId: 'virtualCard',
            title: 'The Virtual Card',
            description: 'Once the group is funded, this is where you reveal card details, copy information, or add the card to your wallet.',
            placement: 'bottom',
        },
        {
            id: 'groupGoal',
            targetId: 'groupGoal',
            title: 'Goal Progress',
            description: 'This progress view shows how close the group is to its target so you always know whether funding is on track.',
            placement: 'bottom',
        },
        {
            id: 'liveActivity',
            targetId: 'liveActivity',
            title: 'Live Activity',
            description: 'This feed expands inline so you can quickly review joins, payments, and purchases without jumping around the page.',
            placement: 'bottom',
        },
        {
            id: 'transactions',
            targetId: 'transactions',
            title: 'Transactions',
            description: 'Every purchase appears here in order, along with the remaining balance, so organizers can review spending clearly.',
            placement: 'top',
        },
        {
            id: 'participants',
            targetId: 'participants',
            title: 'Participants & Pledges',
            description: 'This section shows who has joined, who is still open, and the actions you can use to invite or add more people.',
            placement: 'top',
        },
        {
            id: 'manageGroup',
            targetId: 'manageGroup',
            title: 'Manage Group',
            description: 'When you need to make changes, open the manage drawer from here instead of interrupting the walkthrough automatically.',
            placement: 'top',
            ctaLabel: 'Open Manage Group',
            ctaAction: 'openManageGroup',
        },
        {
            id: 'closingRefunds',
            targetId: 'manageGroup',
            title: 'Closing & Refunds',
            description: 'When spending is done, review the close-group flow to see how charges finalize and how any unused funds are returned.',
            placement: 'top',
            ctaLabel: 'Review Closing & Refunds',
            ctaAction: 'openCloseGroupPreview',
            nextLabel: 'Create My First Group',
        },
    ]), [])

    useEffect(() => {
        if (isFailedResponse(actionData)) {
            switch (actionData.name) {
                case 'ServiceOutageError':
                    setServiceOutage(true)
                    break;
                default:
                    setErrorData(actionData)
                    setError(true)
                    break;
            }
        }
    }, [actionData])

    useEffect(() => {
        // If the user has yet to be asked about push notifications we prompt them here and if
        // permission is granted we log them into the OneSignal client.
        async function notificationPermissions() {
            const options: Parameters<typeof OneSignal.login>[0] = { userId: user?.id || '' }

            const { state } = await OneSignal.checkPermissions()
            if (state === 'prompt') {
                const { granted } = await OneSignal.requestPermissions()

                if (granted && user) {
                    // Log the user into the OneSignal client
                    await OneSignal.login(options)

                    // Update the user's notifications to disable sms and enable push
                    await VirtualCardAPI.UpdateNotifSettings.fetch(user.id, {
                        notifications: user.publicMetadata.settings && user.publicMetadata.settings.notifications
                            ? user.publicMetadata.settings.notifications.map(notif => {
                                return { ...notif, enabled: false, smsEnabled: false, pushEnabled: true }
                            })
                            : []
                    })
                }
            }
        }

        if (sessionStorage.getItem('showNotificationPrompt') === 'true') {
            sessionStorage.removeItem('showNotificationPrompt')
            notificationPermissions()
        }

        if (searchParams.get('fromInvite') === 'true' && group.type === 'fixed') {
            setIsInviteDrawerOpen(true)
            setSearchParams(params => {
                params.delete('fromInvite')
                return params
            })
        }

        if ((searchParams.get('fromMerchantGroupCreate') === 'true' || searchParams.get('fromInvite') === 'true') && group.type === 'merchant') {
            setIsInviteDrawerOpen(true)
            setSearchParams(params => {
                params.delete('fromMerchantGroupCreate')
                params.delete('fromInvite')
                return params
            })
        }
    }, [])

    const { userPledges, groupPledges, claimedPledges, openPledges } = useMemo(() => [...(group.pledges || [])].reduce<{ userPledges: Pledge[], groupPledges: Pledge[], claimedPledges: Pledge[], openPledges: Pledge[] }>((prev, pledge) => {
        if (isSignedIn && isNonEmptyString(user.id)) {
            if (pledge.user?.id === user.id) return { ...prev, userPledges: [...prev.userPledges, pledge], claimedPledges: [...prev.claimedPledges, pledge] };
            else return { ...prev, groupPledges: [...prev.groupPledges, pledge], claimedPledges: (!!pledge.transactionId || !!pledge.user?.id) ? [...prev.claimedPledges, pledge] : prev.claimedPledges, openPledges: (!pledge.transactionId && !pledge.user?.id) ? [...prev.openPledges, pledge] : prev.openPledges }
        } else return prev
    }, { userPledges: [], groupPledges: [], claimedPledges: [], openPledges: [] }), [isSignedIn, user?.id, group.pledges])

    const remainingToGoal = useMemo(() => {
        return Math.max(((group.targetAmount || 0) - (group.collectedAmount || 0)) / 100, 0)
    }, [group.targetAmount, group.collectedAmount])

    const isGroupMember = !!userPledges.length
    const isParticipantClosed = isGroupMember && userPledges[0]?.closedAt !== '0001-01-01T00:00:00Z'
    const shouldCheckPromoChecklist = isOrganizer && !isMerchant && !!user?.id
    const { data: userCredits, isPending: isPromoCreditsPending } = useUserCredits(user?.id)
    const firstGroupPromoCredit = useMemo(() => (
        userCredits?.status === 'successful'
            ? userCredits.credits.find((credit) => credit.action === 'first_group')
            : undefined
    ), [userCredits])
    const { data: organizerActiveGroups = [], isPending: isPromoActiveGroupsPending } = useQuery({
        queryKey: ['group-status-promo-active-groups', user?.id],
        queryFn: () => VirtualCardAPI.GetUserGroupsRequest.fetch(user!.id, { page: 1, size: 100, status: 'active' }),
        enabled: shouldCheckPromoChecklist,
        staleTime: 600000,
    })
    const { hasHistoricalGroups, isPending: isPromoHistoryPending } = useHasHistoricalGroups(
        user?.id,
        shouldCheckPromoChecklist,
        VirtualCardAPI.GetUserGroupsRequest.statuses.filter((status) => status !== 'active')
    )
    const isPromoChecklistLoading = shouldCheckPromoChecklist && (isPromoCreditsPending || isPromoActiveGroupsPending || isPromoHistoryPending)
    const fallbackPromoSpendAmount = useMemo(() => {
        const transactionTotal = Object.values(group.transactions || {}).reduce((sum, relatedTransactions) => {
            return sum + getTransactionSpend(relatedTransactions)
        }, 0)

        return Math.max(group.spendAmount || 0, transactionTotal)
    }, [group.spendAmount, group.transactions])
    const promoChecklist = useMemo<GroupPromoChecklist | undefined>(() => {
        if (!shouldCheckPromoChecklist) return
        if (!firstGroupPromoCredit) return
        if (hasHistoricalGroups) return
        if (organizerActiveGroups.length !== 1) return
        if (organizerActiveGroups[0]?.id !== group.id) return

        const participantThreshold = firstGroupPromoCredit.requirements?.participantCount || 3
        const spendThresholdAmount = firstGroupPromoCredit.requirements?.groupSpend || 10000
        const participantProgress = claimedPledges.length
        const spendProgressAmount = fallbackPromoSpendAmount
        const hasMetRequirements = participantProgress >= participantThreshold && spendProgressAmount >= spendThresholdAmount
        const state = isUnclaimedCredit(firstGroupPromoCredit.claimedAt)
            ? (hasMetRequirements ? 'earned' : 'in_progress')
            : 'applied'

        return {
            type: 'first_group_organizer_credit',
            audience: 'organizer',
            state,
            rewardAmount: firstGroupPromoCredit.amount,
            participantThreshold,
            participantProgress,
            participantRule: 'total_people',
            spendThresholdAmount,
            spendProgressAmount,
        }
    }, [
        claimedPledges.length,
        fallbackPromoSpendAmount,
        firstGroupPromoCredit,
        group.id,
        hasHistoricalGroups,
        organizerActiveGroups,
        shouldCheckPromoChecklist,
    ])
    const shouldShowPromoChecklist = isOrganizer
        && !isMerchant
        && !isPromoChecklistLoading
        && !!promoChecklist
        && promoChecklist.type === 'first_group_organizer_credit'
        && promoChecklist.audience === 'organizer'

    const canSplit = useMemo(() => isOrganizer && (!!groupPledges.length || groupStatus === 'pending'), [isOrganizer, groupPledges.length, groupStatus])

    // Transaction information for the current group
    const transactions = useMemo(() => {
        const items = group.transactions
        if (!items) return []

        return Object.keys(items).map(key => items[key]).reduce((a, b) => a.concat(b), [])
    }, [group.transactions])
    const transactionsGrouped = useMemo(() => {
        if (!!group.transactions)
            return Object.keys(group.transactions).map(key => {
                const merchantName = (group.transactions || {})[key].find(transaction => !!transaction.merchant.name)?.merchant.name || ''
                return { key: merchantName, transactions: (group.transactions || {})[key] }
            })
    }, [group.transactions])
    const hasTransactions = useMemo(() => !!transactions && transactions?.length > 0, [transactions])

    const remainingFunds = useMemo(() => {
        const closedAmount = (group.pledges || [])
            .filter(p => p.closedAt !== "0001-01-01T00:00:00Z")
            .map(p => p.amount - p.chargedToCard)
            .reduce((a, b) => a + b, 0)

        const amount = (group.collectedAmount || 0) - closedAmount - (group.spendAmount || 0)
        if (amount === 0)
            return 0

        return amount
    }, [group.collectedAmount, group.spendAmount, group.pledges])

    useEffect(() => {
        console.log("HERE IS GROUP STATUS ", groupStatus, isLocked)
        for (let i = 0; i < transactions.length; i++) {
            if (isOrganizer && canAddAdditionalFunds && transactions[i].declineCode && (transactions[i].amount > remainingFunds)) {
                setAddMoreAmount((transactions[i].amount - remainingFunds))
                setAddMoreMerchant(transactions[i].merchant.name)
                openConfirmAdd()
            }
        }
    }, [canAddAdditionalFunds, isOrganizer, remainingFunds, transactions])

    // Transaction information for the active group if it defined
    const activeGroupTransactions = useMemo(() => {
        if (!!activeGroup?.transactions) {
            const items = activeGroup.transactions
            if (!items) return []

            return Object.keys(items).map(key => items[key]).reduce((a, b) => a.concat(b), [])
        }

        return []
    }, [activeGroup?.transactions])
    const activeGroupHasTransactions = useMemo(() => !!activeGroupTransactions && activeGroupTransactions?.length > 0, [activeGroupTransactions])

    useEffect(() => { setIsIssuingCard(!group.card?.id && groupStatus === 'active' && isOrganizer) }, [group.card?.id, groupStatus, isOrganizer])

    const sortedLedger = useMemo(() => {
        if (!group?.ledger || !group.transactions) return []

        const transactionMap = new Map(
            Object.entries(group.transactions).map(([transactionId, arr]) => [
                transactionId,
                arr.find(transaction => !!transaction.transactedAt)?.transactedAt
            ])
        )

        return [...group.ledger].sort((a, b) => {
            const aDate = new Date(transactionMap.get(a.transactionId) || 0).getTime()
            const bDate = new Date(transactionMap.get(b.transactionId) || 0).getTime()
            return aDate - bDate
        })
    }, [group.ledger, group.transactions])

    const lastTransactionDate = useMemo(() => {
        if (!transactions || transactions.length === 0) return null;

        const dates = transactions
            .filter(t => t.transactedAt)
            .map(t => parseISO(t.transactedAt));

        return dates.length > 0 ? max(dates) : null;
    }, [transactions])

    const daysSinceActivity = useMemo(() => {
        const referenceDate = hasTransactions && lastTransactionDate ? lastTransactionDate : (group.createdAt ? parseISO(group.createdAt) : null)
        if (!referenceDate) return 0

        return differenceInDays(new Date(), referenceDate)
    }, [hasTransactions, lastTransactionDate, group.createdAt])

    const cycleLength = !hasTransactions ? 5 : (isOrganizer ? 3 : 4)
    const currentCycle = daysSinceActivity > 0 ? Math.floor(daysSinceActivity / cycleLength) : 0

    // Check persistent storage for nudge dismissal status on mount
    useEffect(() => {
        checkNudgeDismissed(group.id, currentCycle).then(isDismissed => {
            setIsNudgeDismissed(isDismissed)
        })
    }, [group.id, currentCycle])

    const shouldShowNudge = useMemo(() => {
        if (isDemoGroup(group.id)) return false
        if (groupStatus !== 'active' || isNudgeDismissed || (!isOrganizer && !isGroupMember) || isParticipantClosed) return false

        if (currentCycle < 1) return false

        return true
    }, [groupStatus, isNudgeDismissed, isOrganizer, isGroupMember, hasTransactions, isParticipantClosed, currentCycle])

    const pendingGroupFinalized = useMemo(() => {
        const nonCreditTransactionGroups = Object.entries(group.transactions || {})
            .filter(([transactionId, txs]) => !isSplitcardCreditTransactionGroup(transactionId, txs))
            .map(([, txs]) => txs)

        return groupStatus === 'pending'
            && isOrganizer
            && nonCreditTransactionGroups.length > 0
            && nonCreditTransactionGroups.every(txs => txs.at(-1)?.status === 'finalized')
    }, [groupStatus, isOrganizer, group.transactions])

    const allTransactionsFinalized = useMemo(() => {
        const txGroups = Object.entries(group.transactions || {})
            .filter(([transactionId, txs]) => !isSplitcardCreditTransactionGroup(transactionId, txs))
            .map(([, txs]) => txs)
        if (txGroups.length === 0) return false
        return txGroups.every(txs => txs.at(-1)?.status === 'finalized')
    }, [group.transactions])

    const showCloseGroupButton = useMemo(() => {
        // For merchant groups, only merchants can close
        if (group.type === 'merchant') {
            if (!isMerchant) return false
        } else {
            // For regular groups, only organizers can close
            if (!isOrganizer) return false
        }

        if (groupStatus === 'pending') return true
        if (groupStatus === 'completed' && allTransactionsFinalized) return true
        return false
    }, [isOrganizer, isMerchant, group.type, groupStatus, allTransactionsFinalized])

    // Determine if the ManageGroupDrawer would have any buttons
    // Buttons in drawer:
    // 1. "Add More Funds": groupStatus === 'active' && isLocked && !isMerchant
    // 2. "Edit Goal Amount": groupStatus === 'active' && isOrganizer
    // 3. "Leave Group": groupStatus === 'active' && !isOrganizer
    // 4. "Activate/Cancel/Close": (groupStatus === 'active' || groupStatus === 'pending') && isOrganizer
    const hasDrawerButtons = useMemo(() => {
        if (groupStatus === 'active') return true // All users have at least one button in active state
        if (groupStatus === 'pending' && group.type !== 'merchant' && isOrganizer) return true
        return false
    }, [group.type, groupStatus, isOrganizer])

    const showRecreateFooterButton = useMemo(() => {
        return canCreateGroups && isOrganizer && group.type !== 'merchant' && (groupStatus === 'completed' || groupStatus === 'canceled' || groupStatus === 'finalized')
    }, [canCreateGroups, group.type, groupStatus, isOrganizer])

    const percentage = useMemo(() => {
        if (!group.collectedAmount || !group.targetAmount) return 0;
        else return Math.floor((group.collectedAmount / group.targetAmount) * 100)
    }, [group.targetAmount, group.collectedAmount])

    const feeAmount = 0

    function setShowOutage() {
        if (isManageGroupDrawerOpen) setIsManageGroupDrawerOpen(false)
        setServiceOutage(true)
    }

    const shareUrl = useMemo(() => {
        const sharePath = isDemoMode ? '/group/demo' : `/group/${group.id}/welcome`

        if (platform === 'web') {
            if (document.location.href.includes('http://localhost'))
                return `https://dev.group.splitcard.com${sharePath}`

            return new URL(sharePath, document.location.href).toString()
        }

        let url = `https://group.splitcard.com${sharePath}`
        if (import.meta.env.DEV)
            url = `https://dev.group.splitcard.com${sharePath}`

        return url
    }, [group.id, isDemoMode, platform, document.location.href])

    const { canShare, share } = useShareSheet({ url: shareUrl })
    function handleShare() {
        if (platform === 'web') {
            if (canShare) share();
            else window.navigator.clipboard.writeText(shareUrl)
                .then(() => createToast({ type: 'success', text: 'Link Copied' }))
                .catch(() => createToast({ type: 'error', text: 'Failed to copy link' }))
        } else {
            Share.canShare()
                .then(result => {
                    if (result.value) Share.share({
                        url: shareUrl,
                        title: 'Splitcard Invite: Tap and Pay'
                    })
                })
        }
    }

    const ref = useRef<HTMLDialogElement>(null)
    function openConfirm() {
        if (ref.current) {
            if (isManageGroupDrawerOpen) setIsManageGroupDrawerOpen(false)
            ref.current.showModal()
        }
    }
    function closeConfirm() { if (ref.current) ref.current.close() }

    const addRef = useRef<HTMLDialogElement>(null)
    function openConfirmAdd() { if (addRef.current) addRef.current.showModal() }
    function closeConfirmAdd() { if (addRef.current) addRef.current.close() }

    const waitRef = useRef<HTMLDialogElement>(null)
    function openWait() {
        if (waitRef.current) {
            if (isManageGroupDrawerOpen) setIsManageGroupDrawerOpen(false)
            waitRef.current.showModal()
        }
    }
    function closeWait() { if (waitRef.current) waitRef.current.close() }

    const amazonCompleteRef = useRef<HTMLDialogElement>(null)
    function openAmazonConfirmComplete() { if (amazonCompleteRef.current) amazonCompleteRef.current.showModal() }
    function closeAmazonConfirmComplete() { if (amazonCompleteRef.current) amazonCompleteRef.current.close() }

    const cancelRef = useRef<HTMLDialogElement>(null)
    function openConfirmCancel() {
        if (cancelRef.current) {
            if (isManageGroupDrawerOpen) setIsManageGroupDrawerOpen(false)
            cancelRef.current.showModal()
        }
    }
    function closeConfirmCancel() { if (cancelRef.current) cancelRef.current.close() }

    const feeRef = useRef<HTMLDialogElement>(null)
    function openFeeInfo(e: React.MouseEvent<SVGSVGElement, MouseEvent>) {
        if (feeRef.current) {
            e.stopPropagation()
            toggleBodyScroll(true)
            feeRef.current.showModal()
        }
    }
    function closeFeeInfo() {
        if (feeRef.current) {
            feeRef.current.close()
            toggleBodyScroll(false)
        }
    }

    const leaveRef = useRef<HTMLDialogElement>(null)
    function openConfirmLeave() {
        if (leaveRef.current) {
            if (isManageGroupDrawerOpen) setIsManageGroupDrawerOpen(false)
            toggleBodyScroll(true)
            leaveRef.current.showModal()
        }
    }
    function closeConfirmLeave() {
        if (leaveRef.current) {
            leaveRef.current.close()
            toggleBodyScroll(false)
        }
    }

    const activeGroupRef = useRef<HTMLDialogElement>(null)
    function openActiveGroupWindow() {
        if (activeGroupRef.current) {
            toggleBodyScroll(true)
            activeGroupRef.current.showModal()
        }
    }
    function closeActiveGroupWindow() {
        if (activeGroupRef.current) {
            activeGroupRef.current.close()
            setClosingActiveGroup(false)
            toggleBodyScroll(false)
        }
    }

    const merchantDialogRef = useRef<HTMLDialogElement>(null)
    function openMerchantDialog(transactionId?: string) {
        setLedgerTransactionId(transactionId)
        if (merchantDialogRef.current) {
            toggleBodyScroll(true)
            merchantDialogRef.current.showModal()
        }
    }
    function closeMerchantDialog() {
        if (merchantDialogRef.current) {
            merchantDialogRef.current.close()
            toggleBodyScroll(false)
        }
        setTimeout(() => setLedgerTransactionId(''), 100)
    }

    const contributionDialogRef = useRef<HTMLDialogElement>(null)
    function openContributionDialog() {
        if (contributionDialogRef.current) {
            toggleBodyScroll(true)
            contributionDialogRef.current.showModal()
        }
    }
    function closeContributionDialog() {
        if (contributionDialogRef.current) {
            contributionDialogRef.current.close()
            toggleBodyScroll(false)
        }
    }

    const closeGroupDialogRef = useRef<HTMLDialogElement>(null)
    const closeGroupCardRef = useRef<HTMLDivElement>(null)
    function openCloseGroupDialog() {
        if (closeGroupDialogRef.current) {
            setCloseGroupKey(prev => prev + 1)
            toggleBodyScroll(true)
            closeGroupDialogRef.current.showModal()
            setIsCloseGroupDialogOpen(true)
        }
    }
    function closeCloseGroupDialog() {
        if (closeGroupDialogRef.current) {
            closeGroupDialogRef.current.close()
            toggleBodyScroll(false)
        }
        setIsCloseGroupDialogOpen(false)
    }

    useEffect(() => {
        if (!isDemoMode) return
        if (currentDemoStepId !== 'liveActivity') return

        setIsDemoActivityFeedOpen(true)
    }, [currentDemoStepId, isDemoMode])

    useEffect(() => {
        if (!isDemoMode) return
        if (!isManageGroupDrawerOpen) return
        if (isDemoTourDismissed) return
        if (currentDemoStepId === 'manageGroup') return

        setIsManageGroupDrawerOpen(false)
    }, [currentDemoStepId, isDemoMode, isDemoTourDismissed, isManageGroupDrawerOpen])

    useEffect(() => {
        if (!isDemoMode) return
        if (!isCloseGroupDialogOpen) return
        if (isDemoTourDismissed) return
        if (currentDemoStepId === 'closingRefunds') return

        if (closeGroupDialogRef.current) {
            closeGroupDialogRef.current.close()
            toggleBodyScroll(false)
        }

        setIsCloseGroupDialogOpen(false)
    }, [currentDemoStepId, isCloseGroupDialogOpen, isDemoMode, isDemoTourDismissed])

    async function getActiveGroup() {
        if (isManageGroupDrawerOpen) setIsManageGroupDrawerOpen(false)
        openActiveGroupWindow()
        setGettingActiveGroup(true)

        if (!isNonEmptyString(activeGroupId)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify(activeGroupId)}`
        })

        console.log("Get Group Data:", { activeGroupId })
        const _activeGroup = await VirtualCardAPI.GetGroupRequest.fetch(activeGroupId)
        setActiveGroup(_activeGroup)
        setGettingActiveGroup(false)
        return json('success', { status: 200 })
    }

    async function closeActiveGroup() {
        setClosingActiveGroup(true)

        if (!isNonEmptyString(activeGroupId)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify(activeGroupId)}`
        })

        console.log("Close Group Data:", { activeGroupId })

        if (!activeGroupHasTransactions) await VirtualCardAPI.CancelGroupRequest.fetch(activeGroupId)
        else await VirtualCardAPI.CompleteGroupRequest.fetch(activeGroupId)

        queryClient.invalidateQueries({ queryKey: ['user-groups'] })
        if (user) user.publicMetadata = {
            tera: parseTeraUserData({ ...user.publicMetadata.tera, activeGroup: undefined })
        }
        setClosingActiveGroup(false)
        closeActiveGroupWindow()
        return json('success', { status: 200 })
    }

    const submit = useSubmit()
    function finalizeGroup() {
        if (isManageGroupDrawerOpen) setIsManageGroupDrawerOpen(false)
        const amazonFlag = ['amazon mktplace pmts', 'amazon.com', 'amazon']
        if (transactions.some((transaction) => amazonFlag.some((amazonFlag) => transaction.merchant.name.toLowerCase().includes(amazonFlag)))) openAmazonConfirmComplete()
        else openCloseGroupDialog()
    }

    function handleGroupClose() {
        const formData = new FormData()
        formData.append('form-btn', 'complete-splitcard')
        submit(formData, { method: 'POST' })
    }

    const demoWalkthroughActions: Record<string, () => void> = {
        openManageGroup: () => setIsManageGroupDrawerOpen(true),
        openCloseGroupPreview: () => {
            if (isManageGroupDrawerOpen) {
                setIsManageGroupDrawerOpen(false)
                return
            }

            openCloseGroupDialog()
        },
    }

    function handleDemoWalkthroughComplete() {
        if (isManageGroupDrawerOpen) setIsManageGroupDrawerOpen(false)
        if (closeGroupDialogRef.current?.open) closeCloseGroupDialog()

        navigate('/group')
    }

    function handleDemoWalkthroughExit() {
        if (isManageGroupDrawerOpen) setIsManageGroupDrawerOpen(false)
        if (closeGroupDialogRef.current?.open) closeCloseGroupDialog()

        navigate('/account')
    }

    const [isOpen, setIsOpen] = useState(false)
    function onStateChange(isOpen: boolean) {
        setIsOpen(isOpen)
    }

    function handleBack() {
        navigate('/account')
    }

    const fromRecreate = searchParams.get('fromRecreate') === 'true'

    // New groups with no participant slots should continue to invite first.
    if (group.type === 'pending' && groupStatus === 'active' && !fromRecreate && (group.pledges?.length || 0) <= 1) {
        return <Navigate to={isOrganizer ? `./invite` : `/account`} replace />
    }

    // Group is merchant group and has no participants
    if (groupStatus === 'active' && group.type === 'merchant' && isOrganizer && group?.pledges?.length === 1) return <Navigate to={`./invite`} replace />

    // Invitees & Initial Viewers
    else if (!isOrganizer && !isGroupMember && !isMerchant) {
        if ((groupStatus === 'active' || groupStatus === 'pending') && canJoinGroups) return <Navigate to="./welcome" replace />
        else {
            let statusText = ''
            let helperText = 'You can no longer participate.'
            const showJoinRestrictionCard = (groupStatus === 'active' || groupStatus === 'pending') && !canJoinGroups

            if (showJoinRestrictionCard) {
                statusText = 'This Account Cannot Join Groups'
                helperText = 'Group joining has been limited on your account.'
            }

            switch (groupStatus) {
                case 'completed': statusText = 'This Group Has Been Completed!'; break;
                case 'canceled': statusText = `${organizerName} Has Canceled This Group`; break;
            }

            return (<>
                <Column.Content className="group-banner-section">
                    {showJoinRestrictionCard
                        ? (
                            <Card
                                className='group-status-join-limit-card'
                                style={{ marginTop: '1rem' }}
                            >
                                <div className='flex flex-row items-center gap-4'>
                                    <div
                                        className='bubble-md flex items-center justify-center shrink-0'
                                        style={{ background: 'rgba(204, 138, 16, 0.12)' }}
                                    >
                                        <Icons.Lock tint='var(--splitcard-yellow-800)' width='1.5rem' height='1.5rem' />
                                    </div>
                                    <div className='flex flex-col gap-1'>
                                        <p className='text-body' style={{ color: 'var(--splitcard-yellow-800)' }}>
                                            <strong>{statusText}</strong>
                                        </p>
                                        <p className='text-body-2' style={{ color: 'var(--splitcard-yellow-800)' }}>
                                            {helperText}
                                        </p>
                                    </div>
                                </div>
                            </Card>
                        )
                        : (
                            <div className="flex flex-col items-center" style={{ textAlign: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
                                <h2 style={{ alignSelf: 'center' }}>{statusText}</h2>
                                <p className="text-body-2">{helperText}</p>
                            </div>
                        )
                    }
                    <GroupBanner isOrganizer={false} isGroupMember={false} group={group} groupStatus={groupStatus} isLocked={isLocked} />
                </Column.Content>
                <Column.Content className="group-status-screen">
                    <div className="flex flex-col gap-4">
                        <h2>Who Joined</h2>
                        <Card>
                            <PledgeList group={group} lockedSlots={lockedSlots} nonParticipant={true} />
                        </Card>
                    </div>
                    <Column.Footer hidden />
                </Column.Content>
            </>)
        }
    }

    // Organizer & Participants & Merchant
    else return (<>
        {(groupStatus === 'finalized' || groupStatus === 'canceled') && <Column.Header.Start>
            <a onClick={handleBack}>
                <img src="/icons/arrow-left.svg" alt="back" className="w-[1.4rem] h-[1.4rem]" draggable="false" />
            </a>
        </Column.Header.Start>}
        {(isLocked && (groupStatus === 'active' || groupStatus === 'pending') && (isGroupMember || isMerchant)) && (
            <Column.Content className="group-banner-section">
                <div ref={demoGroupOverviewRef}>
                    <GroupBanner isOrganizer={isOrganizer} isMerchant={isMerchant} isGroupMember={true} participantPledgeClosed={userPledges[0]?.closedAt !== '0001-01-01T00:00:00Z'} group={group} groupStatus={groupStatus} isLocked={isLocked} />
                </div>
            </Column.Content>
        )}
        {((groupStatus === 'active' && !isLocked) || groupStatus === 'completed' || groupStatus === 'finalized' || groupStatus === 'canceled') && (<>
            <Column.Content className='group-banner-section p-0'>
                <div ref={demoGroupOverviewRef}>
                    <GroupBanner isOrganizer={isOrganizer} isMerchant={isMerchant} isGroupMember={isGroupMember} group={group} groupStatus={groupStatus} />
                    {(groupStatus === 'completed' || groupStatus === 'finalized') &&
                        <div className='flex flex-row items-center justify-center'>
                            <p className='text-body text-green-600'><strong>This group is now complete!</strong></p>
                        </div>
                    }
                    {groupStatus === 'canceled' && <h4 className='text-center text-gray-500'>Canceled</h4>}
                    {isOrganizer && groupStatus === 'completed' && !!groupPledges.length && <p style={{ padding: '1rem 3rem 0 3rem', textAlign: 'center' }}>You can adjust the splits below until your purchases finalize. Once they finalize, your group will be notified.</p>}
                </div>
            </Column.Content >
        </>)}
        <Column.Content className="group-status-screen">
            {isDemoMode && (
                <DemoWalkthrough
                    actions={demoWalkthroughActions}
                    onComplete={handleDemoWalkthroughComplete}
                    onDismissChange={setIsDemoTourDismissed}
                    onExit={handleDemoWalkthroughExit}
                    onStepChange={setCurrentDemoStepId}
                    storageKey={`demo-walkthrough:${group.id}`}
                    steps={demoWalkthroughSteps}
                    targets={demoTargets}
                />
            )}
            {shouldShowNudge && <InactivityNudge
                daysSinceActivity={daysSinceActivity}
                isOrganizer={isOrganizer}
                isMerchantGroup={group.type === 'merchant'}
                groupOrganizer={group.organizer}
                hasTransactions={hasTransactions}
                onCloseGroup={hasTransactions ? finalizeGroup : openConfirmCancel}
                onCloseNudge={() => {
                    saveNudgeDismissal(group.id, currentCycle)
                    setIsNudgeDismissed(true)
                }}
                onParticipantLeave={openConfirmLeave}
            />}
            {pendingGroupFinalized && <AdjustLedgerNudge onAdjustLedger={finalizeGroup} isOrganizer={isOrganizer} groupType={group.type} />}
            {(isLocked) && (<>
                {((groupStatus === 'active' || groupStatus === 'pending' || groupStatus === 'completed') && ((group.type !== 'merchant' && isOrganizer) || (group.type === 'merchant' && isMerchant))) && (<>
                    <div ref={demoVirtualCardRef} className="flex flex-col gap-4">
                        <GroupCard group={group} userPledges={userPledges} onConfirmCancel={openConfirmCancel} hideAddMoney={isMerchant || !canAddAdditionalFunds} onCardReady={() => setIsCardReady(true)} />
                        {(group.card?.cardType !== 'virtual' || groupStatus !== 'completed') && (group.type === 'merchant' && !isMerchant) && <AddToWalletButton cardId={group.card?.id} lastFour={group.card?.lastFour} returnUrl={`/group/${group.id}`} disabled={!isCardReady} />}
                    </div>
                    {shouldShowPromoChecklist && promoChecklist && (
                        <GroupPromoChecklist
                            groupId={group.id}
                            promoChecklist={promoChecklist}
                            groupStatus={groupStatus}
                            onInvitePeople={() => setIsInviteDrawerOpen(true)}
                        />
                    )}
                </>)}
                {(groupStatus === 'active' || groupStatus === 'pending' || groupStatus === 'completed' || groupStatus === 'finalized') && (<>
                    {((groupStatus === 'active' || groupStatus === 'pending' || groupStatus === 'completed') && isLocked && !!group.targetAmount) && (<>
                        <div ref={demoGroupGoalRef} className='flex flex-col gap-2 my-4'>
                            <div className='flex flex-row justify-between'>
                                <p className='text-body'><strong><span style={{ color: 'var(--splitcard-green-600)', fontSize: 'x-large' }}>{priceFormatter.format((group.collectedAmount || 0) / 100)}</span> Collected</strong></p>
                                <p className='text-body'>Goal: <strong>{priceFormatter.format((group.targetAmount || 0) / 100)}</strong></p>
                            </div>
                            <div style={{ width: '100%', height: '1.5rem' }}>
                                <ProgressBar.Default
                                    value={Math.min(((group.collectedAmount || 0) / 100), ((group.targetAmount || 0) / 100))}
                                    max={(group.targetAmount || 0) / 100}
                                    settings={{ fillColor: 'linear-gradient(to right, #4ADE80 80%, #22D3EE) 100%' }}
                                    style={{ height: '100%' }}
                                />
                            </div>
                        </div>
                    </>)}

                    {group.activities && group.activities.length > 0 && (
                        <div ref={demoLiveActivityRef}>
                            <LiveActivityFeed
                                activities={group.activities}
                                groupStatus={groupStatus}
                                onOpenChange={isDemoMode ? setIsDemoActivityFeedOpen : undefined}
                                open={isDemoMode ? isDemoActivityFeedOpen : (group.type === 'merchant' && !isMerchant)}
                            />
                        </div>
                    )}

                    {hasTransactions && (groupStatus === 'finalized') && (<>
                        <Card className="transaction-details">
                            {transactionsGrouped?.map((transaction, index) => (
                                <React.Fragment key={`${transaction.key}-${index}`}>
                                    <TransactionCell transaction={transaction} />
                                </React.Fragment>
                            ))}
                            <Divider />
                            <div className="flex flex-row items-center justify-between gap-4">
                                <p className='text-body'><strong>Purchase Total</strong></p>
                                <h3>{priceFormatter.format((group.spendAmount || 0) / 100)}</h3>
                            </div>
                        </Card>
                    </>)}
                    {((groupStatus === 'active' || groupStatus === 'pending' || groupStatus === 'completed') && isLocked && hasTransactions) && (
                        <div ref={demoTransactionsRef}>
                            <Card
                                data-demo-target="transactions"
                                header={
                                    <div className='transactions-card-header'>
                                        <p className='text-body'><strong>Transactions</strong></p>
                                        {isOrganizer && canSplit && (
                                            <div className='transactions-card-toggle'>
                                                <span className='text-body-2'>Split Total Charges</span>
                                                <Switch
                                                    checked={isContributionMode}
                                                    onChange={(e) => setIsContributionMode(e.target.checked)}
                                                    aria-label='Toggle contribution split mode'
                                                />
                                            </div>
                                        )}
                                    </div>
                                }
                            >
                                {isContributionMode && (
                                    <>
                                        <div className='flex flex-row items-center justify-between cursor-pointer transaction-total-row' onClick={openContributionDialog}>
                                            <div className='flex flex-row items-center gap-4'>
                                                <MerchantLogo merchantCategory={'unknown'} />
                                                <div className='flex flex-col text-left'>
                                                    <p className='text-body' style={{ fontWeight: 'bold' }}>Total Cost</p>
                                                    <p className='text-body text-gray-500'>{priceFormatter.format((group.spendAmount || 0) / 100)}</p>
                                                </div>
                                            </div>
                                            <div className='flex flex-col items-end gap-1'>
                                                <div className='flex flex-row items-center gap-2' style={{ fontWeight: 'bold' }}>
                                                    <Icons.Edit width='1rem' height='1rem' />
                                                    <p className='text-body'>Split</p>
                                                </div>
                                            </div>
                                        </div>
                                        <Divider />
                                        <dialog ref={contributionDialogRef}>
                                            <div>
                                                <div className="temp-card" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 4rem)', padding: '1.5rem', textAlign: 'left', margin: '1rem', maxHeight: '85vh', overflowY: 'auto' }}>
                                                    <div className="flex flex-row justify-between items-center" style={{ marginBottom: '1rem' }}>
                                                        <h3>Contribution Split</h3>
                                                        <button onClick={closeContributionDialog} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', outline: 'none', boxShadow: 'none' }}>
                                                            <Icons.Close height="1rem" width="1rem" tint="var(--splitcard-black)" />
                                                        </button>
                                                    </div>
                                                    <TransactionLedgerEditContribution
                                                        canSplit={canSplit}
                                                        group={group}
                                                        sortedLedger={sortedLedger}
                                                        onDrawerClose={closeContributionDialog}
                                                    />
                                                </div>
                                            </div>
                                        </dialog>
                                    </>
                                )}

                                {sortedLedger.map((item, index) => (
                                    <React.Fragment key={`${item.transactionId}-${index}`}>
                                        <LedgerMerchantCell
                                            canSplit={canSplit}
                                            disableSplitAction={isContributionMode}
                                            group={group}
                                            isOrganizer={isOrganizer}
                                            item={item}
                                            handleMerchantDrawerOpen={(transactionId) => openMerchantDialog(transactionId)}
                                            groupStatus={groupStatus}
                                            muted={isContributionMode}
                                        />
                                        <Divider />
                                    </React.Fragment>
                                ))}

                                <dialog ref={merchantDialogRef}>
                                    <div>
                                        <div className="temp-card" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 4rem)', padding: '1.5rem', textAlign: 'left', margin: '1rem', maxHeight: '85vh', overflowY: 'auto' }}>
                                            <div className="flex flex-row justify-between items-center" style={{ marginBottom: '1rem' }}>
                                                <h3>Transaction Details</h3>
                                                <button onClick={closeMerchantDialog} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', outline: 'none', boxShadow: 'none' }}>
                                                    <Icons.Close height="1rem" width="1rem" tint="var(--splitcard-black)" />
                                                </button>
                                            </div>
                                            <TransactionLedgerEditMerchant
                                                isOrganizer={isOrganizer}
                                                canSplit={canSplit}
                                                ledgerTransactionId={ledgerTransactionId || ''}
                                                group={group}
                                                groupStatus={groupStatus}
                                                onDrawerClose={closeMerchantDialog}
                                                onGroupClose={handleGroupClose}
                                                handleShare={handleShare}
                                                isDemo={isDemoMode}
                                            />
                                        </div>
                                    </div>
                                </dialog>
                            </Card>
                        </div>
                    )}
                    {((group.type !== 'merchant' && isOrganizer) || (group.type === 'merchant' && isMerchant)) && (groupStatus === 'finalized') && (
                        <Card>
                            <Link to="card-details" className="flex flex-row flex-wrap items-center justify-between" style={{ color: 'var(--splitcard-blue-900)', gap: '0.6rem 1rem', textDecoration: 'none' }}>
                                <div className='flex flex-row items-center gap-4'>
                                    <div className="bubble-sml" style={{ backgroundImage: 'url(/icons/duotone/card.svg)' }} />
                                    <p className='text-body'>View Card Details</p>
                                </div>
                                <div className="flex flex-col items-center justify-center" style={{ background: 'var(--splitcard-blue-900)', color: 'var(--splitcard-white)', borderRadius: '50%', boxShadow: 'var(--box-shadow)', width: '2rem', height: '2rem' }}>
                                    <Icons.Arrow width="1.25rem" height="1.25rem" facing="right" />
                                </div>
                            </Link>
                        </Card>
                    )}
                </>)}
            </>)}
            {!isLocked && shouldShowPromoChecklist && promoChecklist && (
                <GroupPromoChecklist
                    groupId={group.id}
                    promoChecklist={promoChecklist}
                    groupStatus={groupStatus}
                    onInvitePeople={() => setIsInviteDrawerOpen(true)}
                />
            )}
            {((groupStatus === 'active') && !isLocked && !!group.targetAmount) && (<>
                <div className='flex flex-col' style={{ marginBottom: '2rem' }}>
                    <div className='flex flex-row justify-between'>
                        <p className='text-body'><strong>{priceFormatter.format((group.collectedAmount || 0) / 100)} Collected</strong></p>
                        <p className='text-body'><strong>{priceFormatter.format((group.targetAmount || 0) / 100)}</strong></p>
                    </div>
                    <div style={{ width: '100%', height: '1.5rem' }}>
                        <ProgressBar.Default
                            value={Math.min(((group.collectedAmount || 0) / 100), ((group.targetAmount || 0) / 100))}
                            max={(group.targetAmount || 0) / 100}
                            settings={{ fillColor: 'linear-gradient(to right, #4ADE80 80%, #22D3EE) 100%' }}
                            style={{ height: '100%' }}
                        />
                    </div>
                </div>
            </>)}
            {!!userPledges && !isMerchant &&
                <div className="flex flex-col gap-4">
                    {!isOrganizer && <h3 className='text-center my-4'>Your Contribution</h3>}
                    <Card className={(groupStatus === 'finalized') ? 'contribution-completed' : ''} >
                        <CollapsibleElement state={[isOpen, setIsOpen]} initState="closed" onStateChange={onStateChange}>
                            <div className='flex flex-col gap-4 w-full'>
                                <div className="flex flex-row items-center justify-between">
                                    <div className='flex flex-row items-center justify-start gap-4'>
                                        <Avatar user={user} style={{ height: '2.4rem', minWidth: '2.4rem' }} />
                                        <div className='flex flex-col'>
                                            <p className='text-body-2 text-gray-500'>Your {(groupStatus === 'active' && userPledges[0]?.closedAt === '0001-01-01T00:00:00Z') && 'current'} charge</p>
                                            {!(!isLocked && groupStatus === 'active') &&
                                                <div className='flex flex-col gap-2 items-start'>
                                                    {(groupStatus === 'canceled')
                                                        ? <h2>{priceFormatter.format(0)}</h2>
                                                        : ((groupStatus === 'active' || groupStatus === 'finalized')
                                                            ? hasTransactions
                                                                ? !!group.spendAmount
                                                                    ? <div style={{ display: 'inline' }}>
                                                                        <h2 style={{ display: 'inline' }}>{priceFormatter.format(userPledges[0]?.chargedToCard / 100)}</h2>
                                                                        <h2 style={{ display: 'inline' }}>&nbsp;/&nbsp;{priceFormatter.format(userPledges[0]?.amount / 100)}</h2>
                                                                    </div>
                                                                    : <h2>{priceFormatter.format(userPledges[0]?.amount / 100)}</h2>
                                                                : <h2>{priceFormatter.format(userPledges[0]?.amount / 100)}</h2>
                                                            : <h2>{priceFormatter.format(userPledges[0]?.chargedToCard / 100)}</h2>)}
                                                </div>
                                            }
                                        </div>
                                    </div>
                                    <div className='flex flex-row items-center gap-4'>
                                        {(!isLocked && groupStatus === 'active') &&
                                            <div className="flex flex-col items-stretch flex-1">
                                                <div className="flex flex-col items-end" style={{ gap: 'inherit' }}>
                                                    <h5><strong>{priceFormatter.format(userPledges[0]?.amount / 100)}</strong></h5>
                                                    <p className="text-body-2"><Link to={`./edit/${userPledges[0]?.id}/contribution`}>Edit</Link></p>
                                                </div>
                                            </div>
                                        }
                                        {(groupStatus !== 'canceled') &&
                                            <Icons.Chevron width="1.4rem" height="1.4rem" facing={isOpen ? 'down' : 'up'} />
                                        }
                                    </div>
                                </div>
                                {(groupStatus !== 'canceled') && (<>
                                    <div className='flex flex-row items-center gap-2'>
                                        <p className="text-body-2" style={{ color: 'var(--splitcard-gray-500)' }}>
                                            <strong>{priceFormatter.format(feeAmount / 100)}&nbsp;</strong>
                                            {(groupStatus === 'active' || groupStatus === 'pending' || groupStatus === 'completed')
                                                ? userPledges[0]?.closedAt !== '0001-01-01T00:00:00Z'
                                                    ? 'is you completed fee based on your share of the cost.'
                                                    : 'is your estimated fee.'
                                                : ((groupStatus === 'finalized')
                                                    ? 'is your fee based on your share of the cost.'
                                                    : '')}
                                        </p>
                                        <Icons.Info onClick={(e) => openFeeInfo(e)} type='filled' height="1rem" width="1rem" style={{ color: 'var(--splitcard-blue-500)', cursor: 'pointer' }} />
                                        <dialog ref={ref}>
                                            <div>
                                                <div className="flex flex-col items-stretch" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem', background: 'var(--splitcard-white)', gap: '1rem', padding: '1.3125rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                                                    <h2>Splitcard Fee</h2>
                                                    <p><strong>Good news — we’ve waived our fee!</strong> Splitcard is free to use for a limited time.</p>
                                                    <p>Now’s the perfect time to try it and <strong>share with friends and family</strong></p>
                                                    <div className="flex flex-col items-stretch gap-2.5">
                                                        <Button theme="primary" formMethod="dialog" onClick={closeFeeInfo}>Got it!</Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </dialog>
                                    </div>
                                </>)}
                            </div>
                            {(groupStatus !== 'canceled') &&
                                <div className='flex flex-col gap-2 content' style={{ marginTop: '1rem' }}>
                                    <div className='flex flex-col gap-2'>
                                        <Divider />
                                        <p className='text-body-2 text-gray-500'><strong>Funding Sources:</strong></p>
                                        <div className='additional-funds-grid'>
                                            <div className='additional-funds'>
                                                {(() => {
                                                    const baseAmount = (groupStatus === 'completed' || groupStatus === 'finalized' || userPledges[0]?.closedAt !== '0001-01-01T00:00:00Z')
                                                        ? (userPledges[0]?.chargedToCard - userPledges[0]?.additionalCharges.reduce((prev, current) => { return prev + current.chargedToCard }, 0))
                                                        : (userPledges[0]?.amount - userPledges[0]?.additionalCharges.reduce((prev, current) => { return prev + current.amount }, 0))

                                                    const creditAmount = (userPledges[0] as any)?.credit?.amount || 0
                                                    const displayAmount = Math.max(0, baseAmount - creditAmount)

                                                    return (<>
                                                        {displayAmount > 0 && <>
                                                            <PaymentMethodBrand
                                                                brand={userPledges[0]?.paymentMethod?.type === 'credit_card'
                                                                    ? userPledges[0]?.paymentMethod?.cardType || userPledges[0]?.paymentMethod?.type
                                                                    : userPledges[0]?.paymentMethod?.type
                                                                }
                                                                height='2rem' width='2rem' style={{ height: '2rem' }}
                                                            />
                                                            <div className='flex flex-col'>
                                                                <p className='text-body-2' style={{ textTransform: 'capitalize' }}>
                                                                    <strong>{
                                                                        `${userPledges[0]?.paymentMethod?.type === 'credit_card'
                                                                            ? (userPledges[0]?.paymentMethod?.name || userPledges[0]?.paymentMethod?.cardType?.replace('_', ' '))
                                                                            : userPledges[0]?.paymentMethod?.type.replace('_', ' ')}`
                                                                    }</strong>
                                                                </p>
                                                                <p className='text-body-2 text-gray-500'>{format(userPledges[0]?.createdAt, "MMM dd, p")}</p>
                                                            </div>
                                                            <p className='text-body-2 text-gray-500'>**** {userPledges[0]?.paymentMethod?.lastFour}</p>
                                                            <div className='flex flex-col text-right'>
                                                                <p className='text-body'><strong>{priceFormatter.format(displayAmount / 100)}</strong></p>
                                                            </div>
                                                        </>}
                                                        {creditAmount > 0 && <>
                                                            <PaymentMethodBrand brand={'splitcard-credit'} height='2rem' width='2rem' style={{ height: '2rem' }} />
                                                            <div className='flex flex-col'>
                                                                <p className='text-body-2' style={{ textTransform: 'capitalize' }}>
                                                                    <strong>Splitcard Credit</strong>
                                                                </p>
                                                                <p className='text-body-2 text-gray-500'>{format(userPledges[0]?.createdAt, "MMM dd, p")}</p>
                                                            </div>
                                                            <p className='text-body-2 text-gray-500'></p>
                                                            <div className='flex flex-col text-right'>
                                                                <p className='text-body'><strong>{priceFormatter.format(creditAmount / 100)}</strong></p>
                                                            </div>
                                                        </>}
                                                    </>)
                                                })()}
                                            </div>
                                            {userPledges[0]?.additionalCharges.map((charge, index) => {
                                                const baseAmount = (groupStatus === 'completed' || groupStatus === 'finalized') ? charge.chargedToCard : charge.amount
                                                const creditAmount = (charge as any).credit?.amount || 0
                                                const displayAmount = Math.max(0, baseAmount - creditAmount)

                                                return (
                                                    <div className='additional-funds' key={`${charge.transactionId}-${index}`}>
                                                        {displayAmount > 0 && <>
                                                            <PaymentMethodBrand
                                                                brand={charge.paymentMethod?.type === 'credit_card'
                                                                    ? charge.paymentMethod?.cardType || charge.paymentMethod?.type
                                                                    : charge.paymentMethod?.type
                                                                }
                                                                height='2rem'
                                                                width='2rem'
                                                                style={{ height: '2rem' }}
                                                            />
                                                            <div className='flex flex-col'>
                                                                <p className='text-body-2' style={{ textTransform: 'capitalize' }}>
                                                                    <strong>{
                                                                        `${charge.paymentMethod?.type === 'credit_card'
                                                                            ? (charge.paymentMethod?.name || charge.paymentMethod?.cardType?.replace('_', ' '))
                                                                            : charge.paymentMethod?.type.replace('_', ' ')}`
                                                                    }</strong>
                                                                </p>
                                                                <p className='text-body-2 text-gray-500'>{format(charge.createdAt, "MMM dd, p")}</p>
                                                            </div>
                                                            <p className='text-body-2 text-gray-500'>**** {charge.paymentMethod?.lastFour}</p>
                                                            <div className='flex flex-col text-right'>
                                                                <p className='text-body'><strong>{priceFormatter.format(displayAmount / 100)}</strong></p>
                                                            </div>
                                                        </>}
                                                        {creditAmount > 0 && <>
                                                            <PaymentMethodBrand
                                                                brand={'splitcard-credit'}
                                                                height='2rem'
                                                                width='2rem'
                                                                style={{ height: '2rem' }}
                                                            />
                                                            <div className='flex flex-col'>
                                                                <p className='text-body-2' style={{ textTransform: 'capitalize' }}>
                                                                    <strong>Splitcard Credit</strong>
                                                                </p>
                                                                <p className='text-body-2 text-gray-500'>{format(charge.createdAt, "MMM dd, p")}</p>
                                                            </div>
                                                            <p className='text-body-2 text-gray-500'></p>
                                                            <div className='flex flex-col text-right'>
                                                                <p className='text-body'><strong>{priceFormatter.format(creditAmount / 100)}</strong></p>
                                                            </div>
                                                        </>}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                            }
                        </CollapsibleElement>
                        {(isOrganizer && groupStatus === 'active' && !isLocked && group.card?.cardType === 'physical') &&
                            <div className='flex flex-row items-center gap-2'>
                                <img src="/icons/physical-card-blue.svg" />
                                <p className="text-body-2 text-gray-500">This group is linked to your physical card</p>
                            </div>
                        }
                        {(isOrganizer && groupStatus === 'active' && !isLocked && (group.card?.id === user?.publicMetadata.tera?.virtualCard?.id)) &&
                            <div className='flex flex-row items-center gap-2'>
                                <img src="/icons/physical-card-blue.svg" />
                                <p className="text-body-2 text-gray-500">This group is linked to your reloadable card</p>
                            </div>
                        }
                    </Card>
                </div>}
            <div ref={demoParticipantsRef} className="flex flex-col gap-4">
                {((groupStatus === 'active' || groupStatus === 'pending' || groupStatus === 'completed') || ((groupStatus === 'finalized') && claimedPledges.length > 1) || ((groupStatus === 'canceled') && (claimedPledges.length > 1 || (group.type === 'merchant' && claimedPledges.length > 0)))) && (
                    !!groupPledges && (claimedPledges.length > 1 || (group.type === 'merchant' && claimedPledges.length > 0)) && <PledgeList group={group} lockedSlots={lockedSlots} showParticipantLabel={isOrganizer} showOutline={true} listType='claimed' />
                )}
                {(groupStatus === 'active') && (isOrganizer || (!isOrganizer && openPledges.length > 0)) &&
                    <div className="flex flex-col gap-4">
                        <Card style={{ gap: ((claimedPledges.length <= 1) || (openPledges.length > 0)) ? '1rem' : '0' }}>
                            {!!groupPledges && <PledgeList group={group} lockedSlots={lockedSlots} showUnclaimedParticipants={true} showParticipantLabel={true} listType='unclaimed' />}
                            {(isOrganizer || isMerchant) && (<>
                                <AnchorLink className='invite-container' onClick={() => setIsInviteDrawerOpen(true)}>
                                    <div className='icon'><Icons.Share width="1.25rem" height="1.25rem" /></div>
                                    <div className='text-center'><p className='text-body'>Send Payment Link</p></div>
                                </AnchorLink>
                                {!isMerchant && (<>
                                    <Divider style={{ margin: ((claimedPledges.length <= 1)) ? '0' : '0.25rem 0' }} />
                                    <Link to={`invite`} className='invite-container'>
                                        <div className='icon'><Icons.Plus width="1.25rem" height="1.25rem" /></div>
                                        <div className='text-center'><p className='text-body-2'><strong>Add More People</strong></p></div>
                                    </Link>
                                </>)}
                            </>)}
                        </Card>
                    </div>
                }
            </div>

            {groupStatus === 'canceled' && <p className='text-body-2 text-center text-gray-500'>This group has been canceled and everyone in the group will not be charged.</p>}

            <dialog ref={feeRef}>
                <div>
                    <div className="temp-card" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem' }}>
                        <h2>Splitcard Fee</h2>
                        <p><strong>Good news — we’ve waived our fee!</strong> Splitcard is free to use for a limited time.</p>
                        <p>Now’s the perfect time to try it and <strong>share with friends and family</strong></p>
                        <Button theme="primary" onClick={closeFeeInfo}>Got it!</Button>
                    </div>
                </div>
            </dialog>
            {(isOrganizer && isLocked && canAddAdditionalFunds && (groupStatus === 'active' || groupStatus === 'pending')) && (
                <dialog ref={addRef}>
                    <div>
                        <Form className="temp-card" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem' }}>
                            <h2>Add Funds</h2>
                            <Card style={{ boxShadow: 'none' }}>
                                <div className='flex flex-row items-center gap-4'>
                                    <div className="bubble-sml" style={{ backgroundImage: 'url(/icons/duotone/dollar-sign.svg)' }} />
                                    <p className='text-body' style={{ width: '75%' }}>You were short <strong>{priceFormatter.format(addMoreAmount / 100)}</strong> for an attempted transaction to "{addMoreMerchant}".</p>
                                </div>
                            </Card>
                            <div className="flex flex-col items-stretch gap-2.5">
                                <Button theme="primary" formMethod="dialog" onClick={() => navigate(`./add/${userPledges[0]?.id}`)}>Add Funds</Button>
                                <Button theme="secondary" formMethod="dialog" onClick={closeConfirmAdd}>Go Back</Button>
                            </div>
                        </Form>
                    </div>
                </dialog>
            )}
            {(isOrganizer && !isLocked) && (
                <dialog ref={ref}>
                    <div>
                        <Form className="temp-card" style={{ padding: '1.25rem', maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem' }}>
                            <h3>You Collected {percentage}% Of Funds Needed</h3>
                            <Card className='activate-modal-explainer'>
                                <div className="bubble-sml" style={{ backgroundImage: 'url(/icons/duotone/dollar-sign.svg)' }} />
                                {(percentage !== 100)
                                    ?
                                    <p className='text-body'>Activating group now will issue your card with an available amount of <strong>{priceFormatter.format((group.collectedAmount || 0) / 100)}</strong>.</p>
                                    :
                                    <p className='text-body'>Activating group now will issue your card with available funds of <strong>{priceFormatter.format((group.collectedAmount || 0) / 100)}</strong>.</p>
                                }
                            </Card>
                            {percentage !== 100 && <p className='text-body'>Go back if you are still waiting for others to join!</p>}
                            <div className="flex flex-col items-stretch gap-2.5">
                                <Button theme="primary" type="submit" formMethod="POST" onClick={closeConfirm}>Activate Card</Button>
                                <Button theme="secondary" formMethod="dialog" onClick={closeConfirm}>Go Back</Button>
                            </div>
                        </Form>
                    </div>
                </dialog>
            )}
            {(isOrganizer && isLocked && (groupStatus === 'active' || groupStatus === 'pending')) && (
                <dialog ref={amazonCompleteRef}>
                    <div>
                        <Form className="temp-card" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem' }}>
                            <h2>Amazon Purchases</h2>
                            <p><strong>Only finalize your group after all your items have shipped.</strong> Amazon doesn't charge your card until shipping, and finalizing too early could result in a failed payment.Once you receive shipping info for all items, then finalize.</p>
                            <div className="flex flex-col items-stretch gap-2.5">
                                <Button theme="primary" formMethod="dialog" onClick={closeAmazonConfirmComplete}>Got It!</Button>
                                <Button name="form-btn" value={!!group.spendAmount ? "complete-splitcard" : "cancel-splitcard"} theme="secondary" type="submit" formMethod="POST" onClick={closeAmazonConfirmComplete}>Finalize</Button>
                            </div>
                        </Form>
                    </div>
                </dialog>
            )}
            {(isOrganizer && isIssuingCard) && (
                <dialog ref={waitRef}>
                    <div>
                        <Form className="temp-card" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem' }}>
                            <h2>Wait!</h2>
                            <p>Your group card is still being issued and is not ready yet. This only takes about 30 seconds. Please try again.</p>
                            <div className="flex flex-col items-stretch gap-2.5">
                                <Button theme="primary" formMethod="dialog" onClick={closeWait}>Okay</Button>
                            </div>
                        </Form>
                    </div>
                </dialog>
            )}
            {(((isOrganizer && group.type !== 'merchant') || isMerchant) && !hasTransactions) && (
                <dialog ref={cancelRef}>
                    <div>
                        <Form className="temp-card" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem' }}>
                            <h2>Cancel Group</h2>
                            <p>Are you sure you want to cancel your group? If you cancel, participants will be notified and nobody will be charged.</p>
                            <div className="flex flex-row gap-2.5">
                                <Button theme="secondary" style={{ flex: 1 }} formMethod="dialog" onClick={closeConfirmCancel}>Go Back</Button>
                                <Button name="form-btn" style={{ flex: 1 }} value="cancel-splitcard" theme="primary" type="submit" formMethod="POST" onClick={closeConfirmCancel}>Cancel Group</Button>
                            </div>
                        </Form>
                    </div>
                </dialog>
            )}
            {(isOrganizer && !!activeGroupId) && (
                <dialog ref={activeGroupRef}>
                    <div>
                        <Form className="temp-card" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem' }}>
                            <h2 style={{ textAlign: 'center' }}>This Group is Linked to Your Card</h2>
                            <p style={{ textAlign: 'center' }}>You can only have one group linked to your {group.card?.cardType === 'physical' ? 'physical' : 'reloadable'} card at a time, you can finalize or tap keep activated to use a one time virtual card.</p>
                            {(gettingActiveGroup || !activeGroup)
                                ? (<div className='flex flex-row items-center justify-center' style={{ gap: '0.5rem', padding: '3rem 0rem' }}>
                                    <Spinner indicator='circle' />
                                </div>)
                                : (<span style={{ pointerEvents: closingActiveGroup ? 'none' : undefined }} onClick={closeActiveGroupWindow}>
                                    <GroupCell group={activeGroup}></GroupCell>
                                </span>)
                            }
                            <div className="flex flex-row gap-2.5">
                                <Button style={{ flex: 1 }} theme="light" disabled={closingActiveGroup} onClick={closeActiveGroupWindow}>Keep Activated</Button>
                                <Button style={{ flex: 1 }} theme="primary" disabled={closingActiveGroup} onClick={closeActiveGroup}>
                                    {!activeGroupHasTransactions
                                        ? (closingActiveGroup ? <div className='flex flex-row justify-center gap-2'><Spinner indicator='circle' /></div> : 'Cancel Group')
                                        : (closingActiveGroup ? <div className='flex flex-row justify-center gap-2'><Spinner indicator='circle' /></div> : 'Close Group')
                                    }
                                </Button>
                            </div>
                        </Form>
                    </div>
                </dialog>
            )}
            {!isOrganizer && (
                <dialog ref={leaveRef}>
                    <div>
                        <Form className="temp-card" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem' }}>
                            <input onChange={() => { }} name="pledgeId" value={userPledges[0]?.id} hidden />
                            <h2 className='text-center'>Leave This Group?</h2>
                            <p className='text-center'>
                                {!hasTransactions
                                    ? `Are you sure you want to leave this group? If you leave, the group organizer (${organizerName}) will be notified and you will not be charged for your contribution towards this group purchase.`
                                    : <>A portion of your funds has already been spent or committed to a purchase. If you leave the group, you will be refunded <strong>{priceFormatter.format((userPledges[0]?.amount - userPledges[0]?.chargedToCard) / 100)}</strong>.</>
                                }
                            </p>
                            <div className="flex flex-row gap-2.5">
                                <Button theme="secondary" style={{ flex: 1 }} formMethod="dialog" onClick={closeConfirmLeave}>Go Back</Button>
                                <Button name="form-btn" style={{ flex: 1 }} value="leave-group" theme="primary" type="submit" formMethod="POST" onClick={closeConfirmLeave}>Leave Group</Button>
                            </div>
                        </Form>
                    </div>
                </dialog>
            )}
            <Drawer open={isManageGroupDrawerOpen} onOpenChange={setIsManageGroupDrawerOpen} onClose={() => setIsManageGroupDrawerOpen(false)}>
                <DrawerContent fullHeight={false}>
                    <DrawerHeader>
                        <DrawerTitle>{(isOrganizer || isMerchant) ? 'Manage Group' : 'Manage Contribution'}</DrawerTitle>
                    </DrawerHeader>
                    <div className="flex flex-col gap-4" style={{ color: 'var(--splitcard-white)', height: 'inherit', padding: '0rem var(--horizontal-content-padding)' }}>
                        {(groupStatus === 'active') && isLocked && !isMerchant && canAddAdditionalFunds && <Button theme='secondary' blueBorder={true} onClick={() => isDemoGroup(group.id) ? toast.info('This action is disabled in the demo') : navigate(`./add/${userPledges[0]?.id}`)}>Add More Funds</Button>}
                        {(groupStatus === 'active') && (isOrganizer || isMerchant) && <Button theme='secondary' blueBorder={true} onClick={() => isDemoGroup(group.id) ? toast.info('This action is disabled in the demo') : navigate(`/group/${group.id}/edit/target`)}>Edit Goal Amount</Button>}
                        {(groupStatus === 'active') && isOrganizer && group.type !== 'merchant' && <Button theme='secondary' blueBorder={true} onClick={() => isDemoGroup(group.id) ? toast.info('This action is disabled in the demo') : navigate(`/group/recreate/${group.id}`)}>Recreate Group</Button>}
                        {(groupStatus === 'active') && !isOrganizer && !isMerchant && <Button theme='dark' onClick={() => isDemoGroup(group.id) ? toast.info('This action is disabled in the demo') : openConfirmLeave()}>Leave Group</Button>}
                        {(groupStatus === 'active' || groupStatus === 'pending') && ((isOrganizer && group.type !== 'merchant') || isMerchant) && (<>
                            {!isLocked ? (<>
                                {<Button style={{ whiteSpace: 'nowrap' }} theme="dark" onClick={() => isDemoGroup(group.id) ? toast.info('This action is disabled in the demo') : (activeGroupId ? getActiveGroup() : (isServicable ? (!isIssuingCard ? openConfirm() : openWait()) : setShowOutage()))}>Activate Card</Button>}
                            </>) : (<>
                                {!hasTransactions
                                    ? <Button theme='dark' onClick={() => isDemoGroup(group.id) ? toast.info('This action is disabled in the demo') : openConfirmCancel()}>Cancel Group</Button>
                                    : <Button theme="dark" onClick={() => isDemoGroup(group.id) ? toast.info('This action is disabled in the demo') : finalizeGroup()}>Close Group</Button>
                                }
                            </>)}
                        </>)}
                    </div>
                    <DrawerFooter>
                        <DrawerClose asChild>
                            <Button className='w-full' theme="secondary" blueBorder={true}>Close</Button>
                        </DrawerClose>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>
            <Drawer open={isInviteDrawerOpen} onClose={() => setIsInviteDrawerOpen(false)}>
                <DrawerContent fullHeight={false} className='max-[450px]:!px-8'>
                    <DrawerHeader showCloseButton>
                        <DrawerTitle className='sr-only'>Invite Participants</DrawerTitle>
                    </DrawerHeader>
                    <div className='flex flex-col items-center justify-center gap-4 text-white text-center'>
                        <h3>Scan or send payment link to collect funds</h3>
                        <div className='my-12 flex w-[80%] flex-col gap-4 max-[450px]:w-full'>
                            <Button theme="secondary" onClick={openQRCodeConfirm} >
                                <div className='flex flex-row items-center gap-4 px-8 max-[450px]:px-8'>
                                    <Icons.QRCode height="2rem" width="2rem" />
                                    <Separator orientation='vertical' />
                                    <h3>Scan QR Code</h3>
                                </div>
                            </Button>
                            <Button theme="secondary" onClick={handleShare} >
                                <div className='flex flex-row items-center gap-4 px-8 max-[450px]:px-8'>
                                    <Icons.Share height="2rem" width="2rem" />
                                    <Separator orientation='vertical' />
                                    <h3>Copy Link</h3>
                                </div>
                            </Button>
                        </div>
                        {!isMerchant && <p className='text-body'>
                            You have <strong>{priceFormatter.format(remainingToGoal)}</strong> remaining to collect to reach your goal.
                        </p>}
                    </div>
                    <dialog id='invite-qr-code' ref={qRCodeRef} onClick={closeQRCodeConfirm}>
                        <div>
                            <div className='flex flex-col'>
                                <div className="bubble-xxxsml flex items-center justify-center" style={{ cursor: 'pointer', alignSelf: 'flex-end' }}>
                                    <Icons.Close height="1rem" width="1rem" tint="var(--splitcard-black)" />
                                </div>
                                <div className="flex flex-col justify-center" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '0.75rem 2rem', background: 'var(--splitcard-white)', gap: '1rem', padding: '1.3125rem', border: '4px solid var(--splitcard-blue-500)', borderRadius: 'var(--border-radius-md)', overflowY: 'auto', position: 'relative' }}>
                                    <QRCodeSVG value={shareUrl} level="H" imageSettings={{ src: '/splitcard/symbol/black-and-blue-fallback.png', height: 40, width: 40, excavate: true }} />
                                </div>
                            </div>
                        </div>
                    </dialog>
                </DrawerContent>
            </Drawer>
            {(showRecreateFooterButton || (!isEditingParticipant && !isParticipantClosed && (showCloseGroupButton || hasDrawerButtons))) && <Column.Footer hidden={false}>
                {showRecreateFooterButton
                    ? <Button onClick={() => isDemoGroup(group.id) ? toast.info('This action is disabled in the demo') : navigate(`/group/recreate/${group.id}`)}>Recreate Group</Button>
                    : (showCloseGroupButton
                        ? <Button onClick={() => isDemoMode ? setIsManageGroupDrawerOpen(true) : finalizeGroup()}>Close Group</Button>
                        : <Button ref={demoManageGroupRef} onClick={() => setIsManageGroupDrawerOpen(true)}>{(isOrganizer || isMerchant) ? 'Manage Group' : 'Manage Contribution'}</Button>
                    )
                }
            </Column.Footer>}

            <dialog ref={closeGroupDialogRef} onClose={() => { toggleBodyScroll(false); setIsCloseGroupDialogOpen(false) }}>
                <div>
                    <div ref={closeGroupCardRef} className="temp-card w-[calc(100vw-2rem)] max-w-[calc(var(--max-content-width,610px)-2rem)] sm:w-[calc(var(--max-content-width,610px)-2rem)]" style={{ padding: '1.5rem', textAlign: 'left', margin: '1rem', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="flex flex-row justify-between items-center" style={{ marginBottom: '1rem' }}>
                            <h3>Close Group</h3>
                            <button onClick={closeCloseGroupDialog} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', outline: 'none', boxShadow: 'none' }}>
                                <Icons.Close height="1rem" width="1rem" tint="var(--splitcard-black)" />
                            </button>
                        </div>
                        <TransactionLedgerCloseGroup
                            key={closeGroupKey}
                            canSplit={canSplit}
                            group={group}
                            groupStatus={groupStatus}
                            sortedLedger={sortedLedger}
                            onDrawerClose={closeCloseGroupDialog}
                            onGroupClose={handleGroupClose}
                            handleShare={handleShare}
                            isDemo={isDemoMode}
                        />
                        {isDemoMode && (
                            <div style={{ background: 'var(--splitcard-blue-500)', borderRadius: 'var(--border-radius-md, 0.75rem)', padding: '1rem', marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                <p style={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.5', fontSize: '0.875rem' }}>
                                    When you're done spending, close the group. Review how transactions were split — unused funds are automatically returned.
                                </p>
                                <Button theme="primary" onClick={() => { closeCloseGroupDialog(); try { localStorage.setItem('demo_completed', 'true') } catch {} navigate('/group') }}>
                                    Create My First Group
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </dialog>
        </Column.Content>
        <ErrorModal
            state={[showError, setError]}
            error={error}
            getTitle={(modalError) => (modalError?.name === 'GroupCardNotIssuedError' ? 'Card Not Ready' : 'Wait!')}
            fallbackMessage="We encountered an issue while processing your action. Please try again later!"
        />
        <ServiceOutageModal state={[showOutage, setServiceOutage]} error={(showOutage && isFailedResponse(actionData)) ? actionData : undefined} />
    </>)
}

interface Propss {
    transaction: Propsss
}

interface Propsss {
    key: string;
    transactions: Transaction[];
}

const TransactionCell: FC<Propss> = ({ transaction }) => {
    const { key, transactions } = transaction

    return (
        <div className="transaction-cell">
            {(!!key) && <p>{key}</p>}
            {transactions?.map((transaction, index) => {
                const { id, transactedAt, status, amount } = transaction
                const date = new Date(transactedAt)
                const prev = index - 1
                const next = index + 1
                const isCredit = status === 'Credit'

                return (
                    <div className='flex flex-row w-full' key={`${id}-${index}`}>
                        <p className='flex flex-col' style={{ flex: '1' }}>
                            <span className='text-body-2 text-gray-500'>{!!date ? format(date, "MM/dd/yy @ p") : '--/--/--'}</span>
                        </p>
                        <p className='text-body-2' style={{ color: (status === 'finalized' || isCredit) ? 'var(--splitcard-green-600)' : 'var(--splitcard-gray-500)', padding: '0rem 1rem', textAlign: 'right' }}>{status === 'pending' ? 'Pre-Auth' : status.charAt(0).toUpperCase() + status.slice(1)}</p>
                        {(status === 'pending' && !!transactions[next] && (transactions[next].status === 'finalized') && (amount >= transactions[next].amount))
                            ? <p style={{ width: '5.5rem' }}></p>
                            : (status === 'finalized' && !!transactions[prev] && (transactions[prev].status === 'pending') && (amount > transactions[prev].amount))
                                ? <p className='text-body' style={{ textAlign: 'right', width: '5.5rem' }}>+{priceFormatter.format((amount - transactions[prev].amount) / 100)}</p>
                                : <p className='text-body' style={{ textAlign: 'right', width: '5.5rem' }}>{(!isCredit) ? '' : '+'}{priceFormatter.format(amount / 100)}</p>
                        }
                    </div>
                )
            })}
        </div>
    )
}

interface Propssss {
    canSplit: boolean
    disableSplitAction?: boolean
    group: Group
    isOrganizer: boolean
    item: LedgerLineItem
    handleMerchantDrawerOpen: (transactionId?: string) => void
    groupStatus: "active" | "completed" | "canceled" | "finalized" | "pending"
    muted?: boolean
}

const LedgerMerchantCell: FC<Propssss> = ({ canSplit, disableSplitAction = false, group, isOrganizer, item, handleMerchantDrawerOpen, muted = false }) => {
    const { amount, relatedTransactions } = useMemo(() => {
        let relatedTransactions: Transaction[] = []

        if (!!group.transactions) relatedTransactions = group.transactions[item.transactionId]
        const amount = getTransactionSpend(relatedTransactions)

        return { amount, relatedTransactions }
    }, [item])
    const merchantName = relatedTransactions?.find(transaction => !!transaction.merchant.name)?.merchant.name.toLowerCase().replace(/\b\w/g, char => char.toUpperCase()) || ''
    const merchantCategory = relatedTransactions?.find(transaction => !!transaction.mcc.category)?.mcc.category || 'unknown'
    const latestStatus = relatedTransactions?.at(-1)?.status
    const disableLedgerAction = disableSplitAction || isSplitcardCreditTransactionGroup(item.transactionId, relatedTransactions)
    const isSplitcardCreditRow = isSplitcardCreditTransactionGroup(item.transactionId, relatedTransactions)
    const displayAmount = disableLedgerAction
        ? (relatedTransactions?.find(transaction => transaction.status === 'Credit')?.amount || amount)
        : amount
    const displayAmountLabel = `${isSplitcardCreditRow ? '+' : ''}${priceFormatter.format((displayAmount || 0) / 100)}`
    const latestStatusLabel = isSplitcardCreditRow
        ? 'Credit Applied'
        : (latestStatus ? latestStatus.charAt(0).toUpperCase() + latestStatus.slice(1) : '')

    const needsAdjustment = useMemo(() => {
        if (isSplitcardCreditTransactionGroup(item.transactionId, relatedTransactions)) return false

        const isFinalized = relatedTransactions?.at(-1)?.status === 'finalized' || relatedTransactions?.at(-1)?.status === 'Credit'
        const ledgerSlotsSum = item.breakdown.reduce((prev, current) => prev + current.amount, 0)

        return isFinalized && ledgerSlotsSum !== amount
    }, [item, amount, relatedTransactions])

    return (
        <div className='flex flex-row items-center justify-between' onClick={() => !disableLedgerAction && handleMerchantDrawerOpen(item.transactionId)} style={{ cursor: disableLedgerAction ? 'default' : 'pointer', opacity: muted ? 0.55 : 1 }}>
            <div className='flex flex-row items-center gap-4'>
                {isSplitcardCreditRow ? (
                    <div
                        className='bubble-xxsml'
                        style={{
                            backgroundColor: 'var(--splitcard-white)',
                            backgroundImage: 'url(/splitcard/symbol/black-and-blue-fallback.png)',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '1.8rem',
                            boxShadow: 'inset 0 0 0 1px var(--splitcard-gray-100)',
                        }}
                    />
                ) : (
                    <MerchantLogo merchantCategory={merchantCategory} />
                )}
                <div className='flex flex-col text-left'>
                    <p className='text-body' style={{ fontWeight: 'bold' }}>{merchantName}</p>
                    <p className='text-body' style={{ color: isSplitcardCreditRow ? 'var(--splitcard-green-600)' : 'var(--splitcard-gray-500)' }}>{displayAmountLabel}</p>
                </div>
            </div>
            <div className='flex flex-col gap-2 justify-end'>
                {(needsAdjustment && isOrganizer && !disableLedgerAction) ? (
                    <div style={{
                        backgroundColor: 'var(--splitcard-red-100)',
                        border: '2px solid var(--splitcard-red-500)',
                        borderRadius: '1rem',
                        padding: '0.25rem 0.75rem',
                        color: 'var(--splitcard-red-500)',
                        fontSize: '0.8rem',
                        fontWeight: 'bold'
                    }}>
                        Adjust
                    </div>
                ) : (
                    <div className='flex flex-col items-end' style={{ gap: '0.125rem' }}>
                        <div className='flex flex-row items-center gap-2' style={{ fontWeight: 'bold' }}>
                            {(canSplit && !disableLedgerAction) ? (<>
                                <Icons.Edit width="1rem" height="1rem" />
                                <p className='text-body'>Split</p>
                            </>) : disableLedgerAction ? null : (<>
                                <Icons.View width="1rem" height="1rem" />
                                <p className='text-body'>View</p>
                            </>)}
                        </div>
                    </div>
                )}
                {!!latestStatus && <p className='text-body-2 text-right' style={{ color: (latestStatus === 'finalized' || latestStatus === 'Credit') ? 'var(--splitcard-green-600)' : 'var(--splitcard-red-500)' }}>
                    <strong>{latestStatusLabel}</strong>
                </p>}
            </div>
        </div>
    )
}

const loader = createAuthenticatedLoader(() => json(null, 200))

const action = createAction(async ({ request, params }) => {
    if (isDemoGroup(params.groupId)) return json(null, { status: 200 })
    try {
        const formData = await request.formData()
        const groupId = params.groupId
        const eventType = formData.get('form-btn')
        if (!isNonEmptyString(groupId)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify({ groupId })}`
        })

        if (eventType === 'cancel-splitcard') {
            console.log("Cancel Group Data:", { groupId })

            await VirtualCardAPI.CancelGroupRequest.fetch(groupId)
            queryClient.invalidateQueries({ queryKey: ['user-groups'] })

            return replace(`/group/${groupId}/`)
        }
        else if (eventType === 'complete-splitcard') {
            console.log("Complete Group Data:", { groupId })

            await VirtualCardAPI.CompleteGroupRequest.fetch(groupId)
            queryClient.invalidateQueries({ queryKey: ['user-groups'] })

            return replace(`/group/${groupId}/`)
        }
        else if (eventType === 'leave-group') {
            const pledgeId = formData.get('pledgeId')

            if (!isNonEmptyString(pledgeId)) throw createResponseObj({
                status: 'failed', type: 'local', name: 'missing-resources',
                message: `Missing required resources. ${JSON.stringify({ pledgeId })}`
            })

            console.log("Leave Group Data:", { groupId, pledgeId })
            await VirtualCardAPI.RemovePledgeRequest.fetch(groupId, pledgeId)
            queryClient.invalidateQueries({ queryKey: ['user-groups'] })

            return replace(`/group/${groupId}/`)
        }
        else if (request.method === 'POST') {
            console.log("Lock Group Data:", { groupId })

            await VirtualCardAPI.LockGroupRequest.fetch(groupId)
            queryClient.invalidateQueries({ queryKey: ['user-groups'] })

            return replace(`/group/${groupId}/`)
        } else return json(null, { status: 200 })
    } catch (error) {
        console.error(error)
        return error
    }
})

const StatusRoute: RouteObject = {
    index: true,
    element: <GroupStatusScreen />,
    loader,
    action,
}

export default StatusRoute
